/**
 * Turn a recorded search (SearchDescriptor / SearchHistoryEntry) into the
 * pieces the recent-searches UI renders: a mode badge, the query as chips
 * (with AND/OR operators for keyword searches), and a one-line filter summary.
 *
 * Tolerant of partial payloads — older entries or shape drift degrade to the
 * stored `label` rather than throwing.
 */
import type { SearchDescriptor } from '../types';
import {
  DEFAULT_LETTER_TYPES,
  DEFAULT_LETTER_CORPUS,
  DEFAULT_LETTER_ROLES,
  DEFAULT_LETTER_DIVISIONS,
} from '../constants/filterConstants';

export interface DescribedTerm {
  term: string;
  op?: 'AND' | 'OR'; // operator that precedes this term (undefined for the first)
}

export interface DescribedSearch {
  modeLabel: string; // 'Keyword' | 'Search' | 'Letter'
  mode: string;
  /** Primary one-line text (used when there are no chips). */
  text: string;
  /** Keyword chips with operators; empty for main/letter. */
  terms: DescribedTerm[];
  /** Compact filter summary, e.g. ["Drug: Keytruda", "US, EU", "Section 6"]. */
  filters: string[];
}

type AnyRec = Record<string, unknown>;

function asArr(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x) : [];
}

export function regionSummary(p: AnyRec): string | null {
  const regions = asArr(p['regions']);
  const single = typeof p['region'] === 'string' ? [p['region'] as string] : [];
  const rs = (regions.length ? regions : single).filter(Boolean);
  return rs.length ? rs.join(', ') : null;
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sb = new Set(b);
  return a.every((x) => sb.has(x));
}

/** FDA-letter filter chips — only the ones the user NARROWED from the default
 *  set, so two letter searches that differ by Type/Corpus/Role/Division read as
 *  distinct while a plain default search stays clean (no wall of default chips). */
function letterFilterSummary(p: AnyRec): string[] {
  const defs: Array<[string, string[]]> = [
    ['letter_types', DEFAULT_LETTER_TYPES],
    ['letter_corpus', DEFAULT_LETTER_CORPUS],
    ['letter_roles', DEFAULT_LETTER_ROLES],
    ['letter_divisions', DEFAULT_LETTER_DIVISIONS],
  ];
  const out: string[] = [];
  for (const [key, def] of defs) {
    const vals = asArr(p[key]);
    if (vals.length && !sameSet(vals, def)) out.push(vals.join(', '));
  }
  return out;
}

export function describeSearch(d: Pick<SearchDescriptor, 'mode' | 'label'> & { payload?: AnyRec }): DescribedSearch {
  const p: AnyRec = (d.payload ?? {}) as AnyRec;
  const filters: string[] = [];

  if (d.mode === 'keyword') {
    const groups = Array.isArray(p['groups']) ? (p['groups'] as AnyRec[]) : [];
    const topRows = Array.isArray(p['rows']) ? (p['rows'] as AnyRec[]) : [];
    const rows: AnyRec[] = topRows.length
      ? topRows
      : groups.flatMap((g) => (Array.isArray(g['rows']) ? (g['rows'] as AnyRec[]) : []));
    const innerOp =
      ((groups[0]?.['inner_operator'] as string) || (p['row_operator'] as string) || 'AND') === 'OR'
        ? 'OR'
        : 'AND';
    const terms: DescribedTerm[] = rows
      .map((r) => String((r['keyword'] as string) ?? '').trim())
      .filter(Boolean)
      .map((term, i) => (i > 0 ? { term, op: innerOp } : { term }));

    // Distinguishing, result-affecting attributes — surfaced so two entries
    // with the same term + region but a different match mode / corpus / filter
    // read as DIFFERENT searches instead of looking like duplicates.
    const hasProximity = groups.some((g) => {
      const px = g['proximity'] as AnyRec | null | undefined;
      return !!(px && px['term_a'] && px['term_b']);
    });
    if (hasProximity) filters.push('Proximity');
    if (rows.some((r) => r['use_synonyms'])) filters.push('Synonyms');
    if (rows.some((r) => r['negate'])) filters.push('Exclude');

    const scope = String((rows[0]?.['section_scope'] as string) ?? '').trim();
    if (scope) filters.push(`Section ${scope}`);
    if (p['drug_name']) filters.push(`Drug: ${String(p['drug_name'])}`);
    if (p['manufacturer']) filters.push(`Mfr: ${String(p['manufacturer'])}`);
    const cats = asArr(p['marketing_categories']);
    if (cats.length) filters.push(cats.map((c) => c.toUpperCase()).join('/'));
    const pharmClass = asArr(p['pharmacological_class']);
    if (pharmClass.length) filters.push(`Class: ${pharmClass.join(', ')}`);
    const reg = regionSummary(p);
    if (reg) filters.push(reg);
    const sources = asArr(p['sources']).filter((s) => s !== 'label');
    if (sources.length) filters.push(sources.join(', '));
    // FDA-letter keyword searches: surface narrowed letter filters.
    filters.push(...letterFilterSummary(p));

    return {
      modeLabel: 'Keyword',
      mode: 'keyword',
      text: terms.map((t) => t.term).join(` ${innerOp} `) || d.label,
      terms,
      filters,
    };
  }

  if (d.mode === 'letter') {
    const q = String((p['query'] as string) ?? d.label ?? '');
    const reg = regionSummary(p);
    if (reg) filters.push(reg);
    // Only narrowed letter filters — so two letter searches that differ by
    // Type/Corpus/Role/Division look different (matching their now-distinct
    // dedup fingerprint), while a default search stays uncluttered.
    filters.push(...letterFilterSummary(p));
    return { modeLabel: 'Letter', mode: 'letter', text: q, terms: [], filters };
  }

  // main (Cogni AI search)
  const q = String((p['query'] as string) ?? d.label ?? '');
  if (p['drug_name']) filters.push(`Drug: ${String(p['drug_name'])}`);
  if (p['manufacturer']) filters.push(`Mfr: ${String(p['manufacturer'])}`);
  const reg = regionSummary(p);
  if (reg) filters.push(reg);
  const cats = asArr(p['marketingCategories']);
  if (cats.length) filters.push(cats.join(', '));
  return { modeLabel: 'Search', mode: 'main', text: q, terms: [], filters };
}

/** "just now" / "5m ago" / "3h ago" / "2d ago" / locale date. `now` is
 *  injectable for testing (Date.now() is fine at runtime). */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const m = Math.floor((now - then) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
